// src/pages/reports/FinancialReports.jsx
// Financial reporting dashboard — per-season revenue breakdown (team + club view)

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, orderBy,
  getDoc, getDocs, doc,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useTeam } from '@/context/TeamContext';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  CurrencyDollarIcon,
  BanknotesIcon,
  UserGroupIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  BuildingOffice2Icon,
  TrophyIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

// Lime/pitch design system tokens — keep in sync with the rest of
// the redesigned pages (AcceptInvite, ScheduleGateway, etc.).
const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ─────────────────────────────── helpers ───────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

function fmtCents(cents) {
  if (!cents || cents === 0) return '$0.00';
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Split array into chunks of `size` */
function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

const STATUS_CONFIG = {
  paid:            { label: 'Paid',       color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  confirmed:       { label: 'Confirmed',  color: 'text-blue-700',    bg: 'bg-blue-50 border-blue-200' },
  pending_payment: { label: 'Pending',    color: 'text-amber-700',   bg: 'bg-amber-50 border-amber-200' },
  cancelled:       { label: 'Cancelled',  color: 'text-zinc-500',    bg: 'bg-zinc-50 border-zinc-200' },
  archived:        { label: 'Archived',   color: 'text-zinc-400',    bg: 'bg-zinc-50 border-zinc-100' },
};

const FILTER_TABS = [
  { key: 'all',       label: 'All' },
  { key: 'paid',      label: 'Paid' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'pending',   label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' },
];

/* ─────────────────────────────── stat card ───────────────────────────────
   Compact on mobile (smaller padding, smaller icon tile, smaller value
   font), grows on sm+. Status-tone colors stay (emerald/blue/amber/violet)
   because they semantically encode payment state — coaches read them at
   a glance and switching to lime here would lose that meaning.
*/
function StatCard({ icon: Icon, label, value, sub, color = 'text-blue-600', bg = 'bg-blue-50' }) {
  return (
    <div className="rounded-2xl border border-zinc-100 bg-white shadow-sm px-3 py-3 sm:px-5 sm:py-4 flex items-center gap-2.5 sm:gap-4">
      <div className={clsx('flex h-9 w-9 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-xl', bg)}>
        <Icon className={clsx('h-5 w-5 sm:h-6 sm:w-6', color)} />
      </div>
      <div className="min-w-0">
        <p
          className="text-[10px] sm:text-[11px] font-bold text-zinc-500 uppercase tracking-[0.8px] leading-tight"
          style={{ fontFamily: HEAD_FONT }}
        >
          {label}
        </p>
        <p className="text-lg sm:text-xl font-bold text-zinc-900 truncate leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-[11px] sm:text-xs text-zinc-400 mt-0.5 truncate">{sub}</p>}
      </div>
    </div>
  );
}

/* ─────────────────────────────── view toggle ───────────────────────────── */
function ViewToggle({ clubMode, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center bg-zinc-100 rounded-xl p-1 gap-0.5">
      <button
        onClick={() => onChange(false)}
        className={clsx(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
          !clubMode ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
        )}
      >
        <TrophyIcon className="h-3.5 w-3.5" />
        {t('financialReports.thisTeam')}
      </button>
      <button
        onClick={() => onChange(true)}
        className={clsx(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all',
          clubMode ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
        )}
      >
        <BuildingOffice2Icon className="h-3.5 w-3.5" />
        {t('financialReports.allClubTeams')}
      </button>
    </div>
  );
}

/* ─────────────────────────────── main ─────────────────────────────────── */
export default function FinancialReports() {
  const { currentTeam } = useTeam();
  const { userProfile } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const teamId  = currentTeam?.id;
  const clubId  = userProfile?.clubId ?? null;

  // Localized status label — STATUS_CONFIG is module-level (color/bg only);
  // labels come from i18n. Falls back to the raw status for unknown values.
  const statusLabel = useCallback(
    (status) => t(`financialReports.status.${status}`, STATUS_CONFIG[status]?.label || status),
    [t],
  );

  /* ── View mode ── */
  const [clubMode, setClubMode] = useState(false);

  /* ── Club data (fetched once when clubId known) ── */
  const [clubTeamIds, setClubTeamIds] = useState([]);      // all teamIds in club
  const [teamNames,   setTeamNames]   = useState({});      // { [teamId]: name }
  const [clubLoading, setClubLoading] = useState(false);

  /* ── Seasons ── */
  const [seasons,        setSeasons]        = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [loadingSeasons, setLoadingSeasons] = useState(true);

  /* ── Registrations ── */
  const [registrations, setRegistrations] = useState([]);
  const [loadingRegs,   setLoadingRegs]   = useState(false);

  /* ── UI ── */
  const [filterTab,        setFilterTab]        = useState('all');
  const [search,           setSearch]           = useState('');
  const [showSeasonPicker, setShowSeasonPicker] = useState(false);

  /* ── Fetch club teamIds + team names ── */
  useEffect(() => {
    if (!clubId) return;
    setClubLoading(true);
    getDoc(doc(db, 'clubs', clubId))
      .then(async (snap) => {
        if (!snap.exists()) return;
        const ids = (snap.data().teamIds || []).filter(Boolean);
        setClubTeamIds(ids);

        if (!ids.length) return;
        // Fetch team names in parallel (up to 30 per batch is fine; just use getDoc)
        const teamDocs = await Promise.all(ids.map(id => getDoc(doc(db, 'teams', id))));
        const names = {};
        teamDocs.forEach(d => { if (d.exists()) names[d.id] = d.data().name || d.id; });
        setTeamNames(names);
      })
      .catch(err => console.error('[FinancialReports] club fetch', err))
      .finally(() => setClubLoading(false));
  }, [clubId]);

  /* ── Load seasons (+ programs in club mode) ── */
  useEffect(() => {
    setSeasons([]);
    setSelectedSeason(null);
    setLoadingSeasons(true);

    if (clubMode) {
      // Club mode: load seasons across all teams + programs for club
      if (!clubTeamIds.length && !clubId) { setLoadingSeasons(false); return; }

      const fetches = [];

      // Seasons (chunked in queries)
      if (clubTeamIds.length > 0) {
        const chunks = chunk(clubTeamIds, 30);
        fetches.push(
          ...chunks.map(ids =>
            getDocs(query(
              collection(db, 'seasons'),
              where('teamId', 'in', ids),
              orderBy('createdAt', 'desc')
            )).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data(), _type: 'season' })))
          )
        );
      }

      // Programs for this club
      if (clubId) {
        fetches.push(
          getDocs(query(
            collection(db, 'programs'),
            where('clubId', '==', clubId),
            orderBy('createdAt', 'desc')
          )).then(snap => snap.docs.map(d => ({ id: d.id, ...d.data(), _type: 'program' })))
        );
      }

      Promise.all(fetches)
        .then(results => {
          const list = results
            .flat()
            .sort((a, b) => {
              const tA = toDate(a.createdAt)?.getTime() ?? 0;
              const tB = toDate(b.createdAt)?.getTime() ?? 0;
              return tB - tA;
            });
          setSeasons(list);
          if (list.length > 0) setSelectedSeason(list[0]);
        })
        .catch(err => console.error('[FinancialReports] club seasons/programs', err))
        .finally(() => setLoadingSeasons(false));

    } else {
      // Team mode: realtime listener for this team's seasons
      if (!teamId) { setLoadingSeasons(false); return; }
      const q = query(
        collection(db, 'seasons'),
        where('teamId', '==', teamId),
        orderBy('createdAt', 'desc')
      );
      const unsub = onSnapshot(q, snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setSeasons(list);
        setSelectedSeason(prev => {
          if (!prev && list.length > 0) return list[0];
          // keep selection if still present
          if (prev && list.find(s => s.id === prev.id)) return prev;
          return list[0] ?? null;
        });
        setLoadingSeasons(false);
      });
      return unsub;
    }
  }, [teamId, clubMode, clubTeamIds.join(',')]);

  /* ── Load registrations for selected season or program ── */
  useEffect(() => {
    if (!selectedSeason) { setRegistrations([]); return; }
    setLoadingRegs(true);
    const isProgram = selectedSeason._type === 'program';
    // Security rules require either teamId (team staff) or clubId (club staff)
    // to be present in the query — otherwise the collection-level query is
    // rejected with permission-denied even though per-doc reads would pass.
    const q = isProgram
      ? query(
          collection(db, 'registrations'),
          where('clubId',    '==', selectedSeason.clubId ?? clubId),
          where('programId', '==', selectedSeason.id),
          orderBy('createdAt', 'desc')
        )
      : query(
          collection(db, 'registrations'),
          where('teamId',   '==', selectedSeason.teamId ?? teamId),
          where('seasonId', '==', selectedSeason.id),
          orderBy('createdAt', 'desc')
        );
    return onSnapshot(q, snap => {
      setRegistrations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingRegs(false);
    }, err => {
      console.warn('[FinancialReports] registrations query failed:', err?.code, err?.message);
      setRegistrations([]);
      setLoadingRegs(false);
    });
  }, [selectedSeason?.id, selectedSeason?._type, teamId, clubId]);

  /* ── Switch view mode ── */
  const handleToggleMode = useCallback((toClub) => {
    setClubMode(toClub);
    setFilterTab('all');
    setSearch('');
    setSelectedSeason(null);
    setRegistrations([]);
  }, []);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const paid      = registrations.filter(r => r.status === 'paid');
    const confirmed = registrations.filter(r => r.status === 'confirmed');
    const pending   = registrations.filter(r => r.status === 'pending_payment');
    const active    = registrations.filter(r => r.status !== 'cancelled' && r.status !== 'archived');

    // Cash actually captured from a single registration.
    // For installment plans, only sum slots that Stripe has already charged
    // (status === 'paid'). For pay-in-full, the full finalAmount was captured.
    const getCollected = (r) => {
      const isInstallmentPlan =
        Array.isArray(r.paymentPlan?.schedule) && (r.installmentsTotal ?? 1) > 1;
      if (isInstallmentPlan) {
        return r.paymentPlan.schedule.reduce(
          (s, slot) => s + (slot.status === 'paid' ? (slot.amount ?? 0) : 0), 0
        );
      }
      return r.pricing?.finalAmount ?? r.amountDue ?? 0;
    };

    // Total contracted = full fee regardless of installment progress.
    const getContracted = (r) => r.pricing?.finalAmount ?? r.amountDue ?? 0;

    // "Collected" stat: cash in hand from paid + confirmed regs.
    // Confirmed = first installment landed, more pending — partial cash.
    const totalCollected   = [...paid, ...confirmed].reduce((s, r) => s + getCollected(r), 0);
    const totalContracted  = [...paid, ...confirmed].reduce((s, r) => s + getContracted(r), 0);

    // For confirmed regs specifically: how much is still outstanding?
    const confirmedCollected  = confirmed.reduce((s, r) => s + getCollected(r), 0);
    const confirmedContracted = confirmed.reduce((s, r) => s + getContracted(r), 0);
    const confirmedRemaining  = confirmedContracted - confirmedCollected;

    const pendingValue = pending.reduce((s, r) => s + getContracted(r), 0);

    return {
      totalCollected,
      totalContracted,
      confirmedCollected,
      confirmedContracted,
      confirmedRemaining,
      pendingValue,
      paidCount:      paid.length,
      confirmedCount: confirmed.length,
      pendingCount:   pending.length,
      totalActive:    active.length,
      // expose helper so table rows can compute their own collected figure
      getCollected,
      getContracted,
    };
  }, [registrations]);

  /* ── Filtered list ── */
  const filtered = useMemo(() => {
    let list = [...registrations];
    if (filterTab === 'paid')      list = list.filter(r => r.status === 'paid');
    else if (filterTab === 'confirmed') list = list.filter(r => r.status === 'confirmed');
    else if (filterTab === 'pending')   list = list.filter(r => r.status === 'pending_payment');
    else if (filterTab === 'cancelled') list = list.filter(r => r.status === 'cancelled' || r.status === 'archived');

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.playerName?.toLowerCase().includes(q) ||
        r.parentName?.toLowerCase().includes(q) ||
        r.parentEmail?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [registrations, filterTab, search]);

  /* ── Season / program display label ── */
  const seasonLabel = (s) => {
    const base = s.name || (s._type === 'program' ? t('financialReports.unnamedProgram') : t('financialReports.unnamedSeason'));
    if (!clubMode) return base;
    const tName = teamNames[s.teamId] || '';
    return tName ? `${tName} — ${base}` : base;
  };

  /* ── Export CSV ── */
  const exportCSV = () => {
    if (!filtered.length) { toast.error(t('financialReports.toast.noData')); return; }
    const headers = [
      ...(clubMode ? [t('financialReports.csv.team')] : []),
      t('financialReports.csv.playerName'), t('financialReports.csv.parentName'),
      t('financialReports.csv.email'), t('financialReports.csv.phone'),
      t('financialReports.csv.status'), t('financialReports.csv.baseFee'),
      t('financialReports.csv.discounts'), t('financialReports.csv.amountDue'),
      t('financialReports.csv.registeredDate'),
    ];
    const rows = filtered.map(r => {
      const base      = r.pricing?.baseAmount ?? r.amountDue ?? 0;
      const discounts = (r.pricing?.earlyBirdSaving ?? 0) + (r.pricing?.siblingDiscount ?? 0)
                      + (r.pricing?.returningDiscount ?? 0) + (r.pricing?.financialAidDiscount ?? 0);
      const finalAmt  = r.pricing?.finalAmount ?? r.amountDue ?? 0;
      const regDate   = toDate(r.createdAt);
      return [
        ...(clubMode ? [teamNames[selectedSeason?.teamId] || ''] : []),
        r.playerName || '',
        r.parentName || '',
        r.parentEmail || '',
        r.parentPhone || '',
        statusLabel(r.status) || '',
        (base / 100).toFixed(2),
        (discounts / 100).toFixed(2),
        (finalAmt / 100).toFixed(2),
        regDate ? format(regDate, 'yyyy-MM-dd') : '',
      ];
    });
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${selectedSeason?.name || 'season'}-financials-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('financialReports.toast.csvExported'));
  };

  /* ── Send payment reminder via Scrimly ──
   * Routes to ComposerWizard with type=Payment Due plus a per-row prefilledFor
   * param. ComposerWizard does not yet consume `prefilledFor`; that's tracked
   * as a TODO so it can render a single-recipient default. The route still
   * works today — composer falls back to its own audience picker.
   */
  const sendViaScrimly = (reg) => {
    const targetTeamId = reg.teamId || selectedSeason?.teamId || teamId;
    if (!targetTeamId) {
      toast.error(t('financialReports.toast.noTeamLinked'));
      return;
    }
    const params = new URLSearchParams({
      type: 'Payment Due',
      prefilledFor: reg.id,
    });
    navigate(`/teams/${targetTeamId}/messages/new?${params.toString()}`);
  };

  /* ── Send reminder to ALL unpaid families for the current season ── */
  const sendBulkReminder = () => {
    const targetTeamId = selectedSeason?.teamId || teamId;
    if (!targetTeamId || !selectedSeason) {
      toast.error(t('financialReports.toast.noBulkContext'));
      return;
    }
    const params = new URLSearchParams({
      type: 'Payment Due',
      audience: 'unpaid',
      seasonId: selectedSeason.id,
    });
    navigate(`/teams/${targetTeamId}/messages/new?${params.toString()}`);
  };

  /* ── Guard: no team AND in team mode ── */
  if (!clubMode && !teamId) {
    return (
      <div className="p-8 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15">
          <CurrencyDollarIcon className="h-7 w-7 text-[#0d1f14]/60" />
        </div>
        <p className="text-zinc-600 text-sm">{t('financialReports.selectTeam')}</p>
        {clubId && (
          <button
            onClick={() => handleToggleMode(true)}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#0d1f14] bg-[#b5f03d] hover:bg-[#94cc28] rounded-xl px-4 py-2.5 transition-colors"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            <BuildingOffice2Icon className="h-4 w-4" />
            {t('financialReports.viewAllInstead')}
          </button>
        )}
      </div>
    );
  }

  const isLoading = clubMode ? (clubLoading || loadingSeasons) : loadingSeasons;

  return (
    <div className="p-4 sm:p-6 lg:p-8">

      {/* Header — stacks on mobile so the heading isn't squeezed against
          the Export button; reverts to side-by-side on sm+. */}
      <div className="mb-5 sm:mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h1
            className="uppercase text-[#0d1f14] leading-[1.05]"
            style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 28, letterSpacing: '0.4px' }}
          >
            {t('financialReports.title')}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('financialReports.subtitle')}
          </p>
        </div>
        <button
          onClick={exportCSV}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-zinc-200 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-zinc-700 shadow-sm hover:bg-zinc-50 active:scale-[0.98] transition-all shrink-0 self-start"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span className="hidden xs:inline sm:inline">{t('financialReports.exportCsv')}</span>
          <span className="xs:hidden sm:hidden">{t('financialReports.exportCsvShort')}</span>
        </button>
      </div>

      {/* Club / Team toggle */}
      {clubId && (
        <div className="mb-5">
          <ViewToggle clubMode={clubMode} onChange={handleToggleMode} />
        </div>
      )}

      {/* Club mode notice */}
      {clubMode && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-3 py-2.5 text-sm text-[#0d1f14]">
          <BuildingOffice2Icon className="h-4 w-4 shrink-0 mt-0.5 text-[#0d1f14]/70" />
          <span className="leading-snug">
            {t('financialReports.clubNotice', { count: clubTeamIds.length })}
          </span>
        </div>
      )}

      {/* Season Picker — full-width on mobile so the long club-mode
          labels ("Team Name — Season Name") have room to breathe. */}
      {isLoading ? (
        <div className="h-12 w-full sm:w-64 rounded-xl bg-zinc-100 animate-pulse mb-5" />
      ) : seasons.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-zinc-200 p-8 sm:p-10 text-center mb-6">
          <CalendarIcon className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
          <p className="text-sm text-zinc-500">
            {clubMode
              ? t('financialReports.noSeasonsClub')
              : t('financialReports.noSeasonsTeam')}
          </p>
        </div>
      ) : (
        <div className="mb-5 sm:mb-6 relative w-full sm:w-auto sm:inline-block">
          <button
            onClick={() => setShowSeasonPicker(p => !p)}
            className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-800 shadow-sm hover:bg-zinc-50 active:scale-[0.99] transition-all w-full sm:w-auto sm:min-w-[260px] sm:max-w-sm"
          >
            <span className="flex-1 text-left truncate">
              {selectedSeason ? seasonLabel(selectedSeason) : t('financialReports.selectSeason')}
            </span>
            <ChevronDownIcon className={clsx('h-4 w-4 text-zinc-400 shrink-0 transition-transform', showSeasonPicker && 'rotate-180')} />
          </button>
          {showSeasonPicker && (
            <div className="absolute top-full left-0 right-0 sm:right-auto mt-1 z-20 sm:w-80 max-h-72 overflow-y-auto rounded-xl border border-zinc-200 bg-white shadow-xl">
              {seasons.map(s => (
                <button
                  key={s.id}
                  onClick={() => {
                    setSelectedSeason(s);
                    setShowSeasonPicker(false);
                    setFilterTab('all');
                    setSearch('');
                  }}
                  className={clsx(
                    'w-full text-left px-4 py-2.5 text-sm transition-colors',
                    s.id === selectedSeason?.id
                      ? 'bg-[#b5f03d]/15 text-[#0d1f14] font-bold border-l-4 border-[#b5f03d]'
                      : 'text-zinc-700 hover:bg-zinc-50 border-l-4 border-transparent'
                  )}
                >
                  {clubMode && teamNames[s.teamId] && (
                    <span
                      className="block text-[10px] font-bold uppercase tracking-[0.8px] text-zinc-400 mb-0.5"
                      style={{ fontFamily: HEAD_FONT }}
                    >
                      {teamNames[s.teamId]}
                    </span>
                  )}
                  <span className="flex items-center gap-2 flex-wrap">
                    <span>{s.name || (s._type === 'program' ? t('financialReports.unnamedProgram') : t('financialReports.unnamedSeason'))}</span>
                    {s._type === 'program' && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#b5f03d]/20 text-[#0d1f14]">
                        {t('financialReports.programBadge')}
                      </span>
                    )}
                  </span>
                  {s.sport && <span className="text-xs text-zinc-400 font-normal">· {s.sport}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Click-outside to close picker */}
      {showSeasonPicker && (
        <div className="fixed inset-0 z-10" onClick={() => setShowSeasonPicker(false)} />
      )}

      {/* Season/program context label in club mode */}
      {clubMode && selectedSeason && (
        <div className="mb-4 flex items-center gap-1.5 text-xs text-zinc-500">
          {selectedSeason._type === 'program' ? (
            <>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">{t('financialReports.programBadge')}</span>
              <span>{t('financialReports.divisions', { count: selectedSeason.divisions?.length ?? 0 })}</span>
            </>
          ) : teamNames[selectedSeason.teamId] ? (
            <>
              <TrophyIcon className="h-3.5 w-3.5" />
              <span>{t('financialReports.teamLabelPrefix')} <span className="font-semibold text-zinc-700">{teamNames[selectedSeason.teamId]}</span></span>
            </>
          ) : null}
        </div>
      )}

      {selectedSeason && (
        <>
          {/* Bulk reminder callout — only when there are 2+ unpaid families.
              On mobile: stacks vertically (icon+text on top, full-width
              button below). On sm+: classic side-by-side. Sticky-top so
              it stays visible while a coach scrolls the registration list. */}
          {stats.pendingCount >= 2 && (
            <div
              role="alert"
              className="mb-4 sticky top-2 z-10 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 shadow-sm"
            >
              <div className="flex items-start sm:items-center gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5 sm:mt-0 text-amber-600" />
                <p className="flex-1 text-sm leading-snug">
                  {t('financialReports.bulkRemind', {
                    count: stats.pendingCount,
                    season: selectedSeason.name || t('financialReports.thisSeason'),
                  })}
                </p>
                <button
                  type="button"
                  onClick={sendBulkReminder}
                  aria-label={t('financialReports.bulkAria', { count: stats.pendingCount })}
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white px-4 text-sm font-bold uppercase tracking-wide transition-colors shrink-0"
                  style={{ minHeight: 48, fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  {t('financialReports.sendToAll', { count: stats.pendingCount })}
                </button>
              </div>
              {/* Mobile-only full-width button below the text so it's easy
                  to tap without competing with the message. */}
              <button
                type="button"
                onClick={sendBulkReminder}
                aria-label={t('financialReports.bulkAria', { count: stats.pendingCount })}
                className="sm:hidden mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white py-3 text-sm font-bold uppercase tracking-wide transition-colors"
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                {t('financialReports.sendReminderToAll', { count: stats.pendingCount })}
              </button>
            </div>
          )}

          {/* Stat Cards */}
          <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              icon={BanknotesIcon}
              label={t('financialReports.stat.collected')}
              value={fmtCents(stats.totalCollected)}
              sub={
                stats.totalContracted !== stats.totalCollected
                  ? t('financialReports.stat.collectedSubContracted', { amount: fmtCents(stats.totalContracted) })
                  : t('financialReports.stat.collectedSubPaid', { count: stats.paidCount })
              }
              color="text-emerald-600"
              bg="bg-emerald-50"
            />
            <StatCard
              icon={CheckCircleIcon}
              label={t('financialReports.stat.confirmedRemaining')}
              value={fmtCents(stats.confirmedRemaining)}
              sub={t('financialReports.stat.confirmedSub', { count: stats.confirmedCount })}
              color="text-blue-600"
              bg="bg-blue-50"
            />
            <StatCard
              icon={ClockIcon}
              label={t('financialReports.stat.pending')}
              value={fmtCents(stats.pendingValue)}
              sub={t('financialReports.stat.pendingSub', { count: stats.pendingCount })}
              color="text-amber-500"
              bg="bg-amber-50"
            />
            <StatCard
              icon={UserGroupIcon}
              label={t('financialReports.stat.totalRegistered')}
              value={stats.totalActive}
              sub={t('financialReports.stat.totalSub')}
              color="text-violet-600"
              bg="bg-violet-50"
            />
          </div>

          {/* Revenue progress bar */}
          {(stats.totalCollected + stats.confirmedRemaining + stats.pendingValue) > 0 && (
            <div className="mb-6 rounded-2xl border border-zinc-100 bg-white shadow-sm px-4 sm:px-5 py-3.5 sm:py-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-2">
                <span
                  className="text-[11px] sm:text-xs font-bold text-zinc-500 uppercase tracking-[0.8px]"
                  style={{ fontFamily: HEAD_FONT }}
                >
                  {t('financialReports.revenueBreakdown')}
                </span>
                <span className="text-[11px] sm:text-xs text-zinc-400">
                  {t('financialReports.revenueExpected', {
                    collected: fmtCents(stats.totalCollected),
                    total: fmtCents(stats.totalCollected + stats.confirmedRemaining + stats.pendingValue),
                  })}
                </span>
              </div>
              {(() => {
                const total   = stats.totalCollected + stats.confirmedRemaining + stats.pendingValue;
                const paidPct = total ? (stats.totalCollected     / total) * 100 : 0;
                const confPct = total ? (stats.confirmedRemaining / total) * 100 : 0;
                const pendPct = total ? (stats.pendingValue       / total) * 100 : 0;
                return (
                  <div className="flex h-3 rounded-full overflow-hidden bg-zinc-100 gap-px">
                    {paidPct > 0 && <div style={{ width: `${paidPct}%` }} className="bg-emerald-500 transition-all" />}
                    {confPct > 0 && <div style={{ width: `${confPct}%` }} className="bg-blue-400 transition-all" />}
                    {pendPct > 0 && <div style={{ width: `${pendPct}%` }} className="bg-amber-300 transition-all" />}
                  </div>
                );
              })()}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2 text-[11px] sm:text-xs text-zinc-500">
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500 inline-block" />{t('financialReports.legendCollected')}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-400 inline-block" />{t('financialReports.legendStillOwed')}</span>
                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-300 inline-block" />{t('financialReports.legendPending')}</span>
              </div>
            </div>
          )}

          {/* Filter tabs + Search — stacked on mobile, side-by-side on
              sm+. Tabs scroll horizontally on tiny screens so all 5 are
              reachable without wrapping. Search gets its own row on
              mobile (full-width with a leading magnifier glyph so it
              doesn't look like a generic textbox). */}
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-2">
            <div className="flex items-center gap-1 bg-zinc-100 rounded-xl p-1 overflow-x-auto scrollbar-thin -mx-1 px-1 sm:mx-0 sm:px-1">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFilterTab(tab.key)}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap shrink-0',
                    filterTab === tab.key
                      ? 'bg-white text-zinc-900 shadow-sm'
                      : 'text-zinc-500 hover:text-zinc-700'
                  )}
                >
                  {t(`financialReports.filters.${tab.key}`)}
                  {tab.key !== 'all' && (
                    <span className="ml-1.5 text-[10px] text-zinc-400">
                      {tab.key === 'paid'      ? stats.paidCount
                       : tab.key === 'confirmed' ? stats.confirmedCount
                       : tab.key === 'pending'   ? stats.pendingCount
                       : registrations.filter(r => r.status === 'cancelled' || r.status === 'archived').length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="relative flex-1 sm:max-w-xs">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                placeholder={t('financialReports.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-3 py-2 text-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/50 focus:border-[#b5f03d]"
              />
            </div>
          </div>

          {/* Registration list — two completely separate layouts:
              • Mobile (<sm): card-per-registration with clear visual
                hierarchy and a full-width reminder button.
              • Desktop (sm+): the existing scannable table.
          */}
          {loadingRegs ? (
            <div className="space-y-2">
              {[1,2,3,4,5].map(n => <div key={n} className="h-20 sm:h-14 rounded-xl bg-zinc-100 animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-zinc-200 p-8 sm:p-10 text-center">
              <UserGroupIcon className="mx-auto h-8 w-8 text-zinc-300 mb-2" />
              <p className="text-sm text-zinc-500">{t('financialReports.noMatch')}</p>
            </div>
          ) : (
            <>
              {/* ── Mobile: card list ─────────────────────────────── */}
              <div className="sm:hidden space-y-2">
                {filtered.map(reg => {
                  const statusMeta = STATUS_CONFIG[reg.status] || { label: reg.status, color: 'text-zinc-500', bg: 'bg-zinc-50 border-zinc-200' };
                  const contracted = stats.getContracted(reg);
                  const collected  = stats.getCollected(reg);
                  const isInstallmentPlan =
                    Array.isArray(reg.paymentPlan?.schedule) && (reg.installmentsTotal ?? 1) > 1;
                  const isPartiallyPaid = isInstallmentPlan && collected < contracted && collected > 0;
                  const regDate    = toDate(reg.createdAt);
                  const isPending  = reg.status === 'pending_payment';
                  const playerName = reg.playerName
                    || [reg.playerFirstName, reg.playerLastName].filter(Boolean).join(' ')
                    || '—';

                  return (
                    <div
                      key={reg.id}
                      className="rounded-2xl border border-zinc-100 bg-white shadow-sm p-3.5 active:bg-zinc-50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="min-w-0 flex-1">
                          <p className="text-[15px] font-bold text-zinc-900 truncate leading-tight">{playerName}</p>
                          {reg.division && (<p className="text-[11px] text-zinc-400 mt-0.5">{reg.division}</p>)}
                        </div>
                        <span className={clsx(
                          'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shrink-0',
                          statusMeta.bg, statusMeta.color
                        )}>
                          {statusLabel(reg.status)}
                        </span>
                      </div>

                      <div className="min-w-0 mb-2.5">
                        <p className="text-[13px] text-zinc-700 truncate">{reg.parentName || '—'}</p>
                        {reg.parentEmail && (<p className="text-[11px] text-zinc-400 truncate">{reg.parentEmail}</p>)}
                      </div>

                      <div className="flex items-end justify-between gap-3 pt-2 border-t border-zinc-50">
                        <div className="min-w-0">
                          {contracted > 0 ? (
                            isPartiallyPaid ? (
                              <>
                                <p className="text-[15px] font-bold text-zinc-900 leading-tight">{fmtCents(collected)}</p>
                                <p className="text-[10px] text-zinc-400">{t('financialReports.ofAmount', { amount: fmtCents(contracted) })}</p>
                              </>
                            ) : (
                              <>
                                <p className="text-[15px] font-bold text-zinc-900 leading-tight">{fmtCents(contracted)}</p>
                                {reg.pricing?.totalDiscount > 0 && (
                                  <p className="text-[10px] text-emerald-600">{t('financialReports.offAmount', { amount: fmtCents(reg.pricing.totalDiscount) })}</p>
                                )}
                              </>
                            )
                          ) : (
                            <p className="text-[14px] font-normal text-zinc-400">{t('financialReports.free')}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p
                            className="text-[10px] font-bold uppercase tracking-wide text-zinc-400 leading-none"
                            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                          >{t('financialReports.registered')}</p>
                          <p className="text-[11px] text-zinc-500 mt-0.5">{regDate ? format(regDate, 'MMM d, yyyy') : '—'}</p>
                        </div>
                      </div>

                      {isPending && contracted > 0 && (
                        <button
                          onClick={() => sendViaScrimly(reg)}
                          aria-label={t('financialReports.sendReminderAria', { name: reg.parentName || playerName })}
                          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] py-2.5 text-xs font-bold uppercase transition-colors"
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                        >
                          <PaperAirplaneIcon className="h-3.5 w-3.5" />
                          {t('financialReports.sendPaymentReminder')}
                        </button>
                      )}
                    </div>
                  );
                })}

                <div className="rounded-2xl border border-zinc-100 bg-zinc-50 px-4 py-3 flex items-center justify-between text-xs">
                  <span className="text-zinc-500">{t('financialReports.regCount', { count: filtered.length })}</span>
                  {(() => {
                    const filteredCollected  = filtered.reduce((s, r) => s + stats.getCollected(r), 0);
                    const filteredContracted = filtered.reduce((s, r) => s + stats.getContracted(r), 0);
                    return (
                      <span className="font-bold text-zinc-700 text-right">
                        {fmtCents(filteredCollected)}
                        {filteredContracted !== filteredCollected && (
                          <span className="block text-[10px] font-normal text-zinc-400">{t('financialReports.ofAmount', { amount: fmtCents(filteredContracted) })}</span>
                        )}
                      </span>
                    );
                  })()}
                </div>
              </div>

              {/* ── Desktop: table ────────────────────────────────── */}
              <div className="hidden sm:block rounded-2xl border border-zinc-100 bg-white shadow-sm overflow-hidden">
                <div className="grid grid-cols-[1fr_1fr_120px_100px_120px_48px] gap-x-4 px-5 py-2.5 bg-zinc-50 border-b border-zinc-100 text-xs font-bold text-zinc-500 uppercase tracking-[0.6px]" style={{ fontFamily: HEAD_FONT }}>
                  <span>{t('financialReports.th.player')}</span>
                  <span>{t('financialReports.th.parent')}</span>
                  <span>{t('financialReports.th.status')}</span>
                  <span className="text-right">{t('financialReports.th.amount')}</span>
                  <span>{t('financialReports.th.registered')}</span>
                  <span />
                </div>

                <div className="divide-y divide-zinc-50">
                  {filtered.map(reg => {
                    const statusMeta = STATUS_CONFIG[reg.status] || { label: reg.status, color: 'text-zinc-500', bg: 'bg-zinc-50 border-zinc-200' };
                    const contracted = stats.getContracted(reg);
                    const collected  = stats.getCollected(reg);
                    const isInstallmentPlan =
                      Array.isArray(reg.paymentPlan?.schedule) && (reg.installmentsTotal ?? 1) > 1;
                    const isPartiallyPaid = isInstallmentPlan && collected < contracted && collected > 0;
                    const regDate    = toDate(reg.createdAt);
                    const isPending  = reg.status === 'pending_payment';

                    return (
                      <div key={reg.id} className="grid grid-cols-[1fr_1fr_120px_100px_120px_48px] gap-x-4 px-5 py-3.5 items-center hover:bg-zinc-50 transition-colors">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{reg.playerName || [reg.playerFirstName, reg.playerLastName].filter(Boolean).join(' ') || '—'}</p>
                          {reg.division && <p className="text-xs text-zinc-400">{reg.division}</p>}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-zinc-700 truncate">{reg.parentName || '—'}</p>
                          <p className="text-xs text-zinc-400 truncate">{reg.parentEmail || ''}</p>
                        </div>
                        <div>
                          <span className={clsx('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold', statusMeta.bg, statusMeta.color)}>
                            {statusLabel(reg.status)}
                          </span>
                        </div>
                        <div className="text-sm font-bold text-zinc-900 text-right">
                          {contracted > 0 ? (
                            isPartiallyPaid ? (
                              <>
                                <span>{fmtCents(collected)}</span>
                                <p className="text-[10px] font-normal text-zinc-400">{t('financialReports.ofAmount', { amount: fmtCents(contracted) })}</p>
                              </>
                            ) : fmtCents(contracted)
                          ) : (<span className="font-normal text-zinc-400">{t('financialReports.free')}</span>)}
                          {reg.pricing?.totalDiscount > 0 && !isPartiallyPaid && (
                            <p className="text-[10px] font-normal text-emerald-600">{t('financialReports.offAmount', { amount: fmtCents(reg.pricing.totalDiscount) })}</p>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400">{regDate ? format(regDate, 'MMM d, yyyy') : '—'}</div>
                        <div className="flex items-center justify-end">
                          {isPending && contracted > 0 && (
                            <button
                              onClick={() => sendViaScrimly(reg)}
                              aria-label={t('financialReports.sendViaScrimlyAria')}
                              title={t('financialReports.sendViaScrimlyTitle')}
                              className="p-1.5 rounded-lg text-[#5a8a1f] hover:text-[#3d6010] hover:bg-[#b5f03d]/15 transition-colors"
                            >
                              <PaperAirplaneIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50 flex items-center justify-between text-xs text-zinc-500">
                  <span>{t('financialReports.regCount', { count: filtered.length })}</span>
                  {(() => {
                    const filteredCollected  = filtered.reduce((s, r) => s + stats.getCollected(r), 0);
                    const filteredContracted = filtered.reduce((s, r) => s + stats.getContracted(r), 0);
                    return (
                      <span className="font-semibold text-zinc-700">
                        {t('financialReports.collectedPrefix', { amount: fmtCents(filteredCollected) })}
                        {filteredContracted !== filteredCollected && (
                          <span className="ml-1 font-normal text-zinc-400">{t('financialReports.ofAmount', { amount: fmtCents(filteredContracted) })}</span>
                        )}
                      </span>
                    );
                  })()}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ── Inline calendar icon (avoids extra import) ── */
function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
    </svg>
  );
}
